import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {Paper, Skeleton,} from "@mui/material";
import {AgGridReactProps} from "ag-grid-react/lib/shared/interfaces";
import i18n from "i18next";
import {AG_GRID_LOCALE_IT} from "../../config/dictionary/agGridLocale_it";
import {AG_GRID_LOCALE_EN} from "../../config/dictionary/agGridLocale_en";
import {ColDef, IStatusPanelParams, StatusPanelDef} from "ag-grid-community";
import {GridReadyEvent} from "ag-grid-community/dist/lib/events";
import AgGridCustomFooterClientData from "./AgGridCustomFooterClientData";
import AgGridCustomFooterServerData, {ICustomServerSidePaginationConfig} from "./AgGridCustomFooterServerData";
import AgGridCustomToolbar, {AgGridCustomToolbarProps} from "./AgGridCustomToolbar";
import {AG_GRID_LOCALE_FR} from "../../config/dictionary/agGridLocale_fr";

/**
 * This component renders a table using the AgGrid library. The component accepts props for the AgGridReact component, as well as props for customizing the table's toolbar and height.
 * The component uses the i18n library for localization, and will automatically use the correct language based on the user's locale.
 * @param props
 * @constructor
 * @see https://www.ag-grid.com/react-grid/
 *
 * A full example can be this:
 *
 * import React from 'react';
 * import {useMemo} from "react";
 * import AgGridCustomTable from './AgGridCustomTable';
 *
 * const gridRef = useRef(null);
 *
 * const columns = [
 *   {
 *     headerName: 'Name',
 *     field: 'name',
 *   },
 *   {
 *     headerName: 'Actions',
 *     field: '_actions',
 *   },
 * ];
 *
 * const data = [
 *   {
 *     name: 'John Doe',
 *   },
 *   {
 *     name: 'Jane Doe',
 *   },
 * ];
 *
 * const toolbar = {
 *   renderInfo: ({ref}:any) => (
 *     <div>
 *       This is the table toolbar.
 *     </div>
 *   ),
 *   renderActions: ({ref}:any) => (
 *     <div>
 *       These are the table actions.
 *     </div>
 *   ),
 * };
 *
 * const defaultColDef = useMemo(() => {
 *         return {
 *             sortable: false,
 *         }
 *     }, []);
 *
 * const Table = () => (
 *   <AgGridCustomTable
 *     ref={gridRef}
 *     toolbar={toolbar}
 *     columns={columns}
 *     data={data}
 *     defaultColDef={defaultColDef}
 *   />
 * );
 *
 * export default Table;
 */

export interface TableProps<TData> extends AgGridReactProps<TData> {
    height?: string,
    toolbar?: AgGridCustomToolbarProps,
    gridRef?: React.RefObject<AgGridReact>,
    customServerSidePaginationConfig?: ICustomServerSidePaginationConfig
}

const AgGridCustomTable = <TData extends any = any>({
                                                        toolbar,
                                                        height,
                                                        gridRef,
                                                        customServerSidePaginationConfig,
                                                        ...agGridProps
                                                    }: TableProps<TData>) => {
    const [tableHeight, setTableHeight] = useState(height || '0px');
    const [render, setRender] = useState(false);
    const [locale, setLocale] = useState(AG_GRID_LOCALE_EN);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<AgGridReact<TData>>(null);
    const ref = gridRef ?? innerRef;
    const defaultColDef = useMemo<ColDef>(() => ({
        suppressMenu: true,
        cellClass: (params: any) => {
            return params.colDef.field === '_actions' ? 'ag-grid-cell-actions' : '';
        },
        headerClass: (params: any) => {
            return params.colDef.field === '_actions' ? 'ag-grid-header-actions' : '';
        },
        sortingOrder: ['desc', 'asc', null],
        ...agGridProps.defaultColDef,
        ...agGridProps.gridOptions?.defaultColDef
    }), [agGridProps.defaultColDef, agGridProps.gridOptions]);

    const handleGridReady = useCallback((event: GridReadyEvent) => {
        event.api.sizeColumnsToFit();
        agGridProps.onGridReady && agGridProps.onGridReady(event);
    }, []);

    const AgGridCustomFooter = (props: IStatusPanelParams, locale: any) => {
        return (customServerSidePaginationConfig !== undefined || agGridProps.rowModelType === 'serverSide' || agGridProps.gridOptions?.rowModelType === 'serverSide') ?
            <AgGridCustomFooterServerData statusPanelParams={props}
                                          customServerSidePaginationConfig={customServerSidePaginationConfig}/> :
            <AgGridCustomFooterClientData statusPanelParams={props}/>;
    }

    const statusBar = useMemo<{ statusPanels: StatusPanelDef[] }>(() => {
        return {
            statusPanels: [
                {
                    statusPanel: AgGridCustomFooter,
                }
            ],
        };
    }, []);

    useEffect(() => {
        setRender(false);
        switch (i18n.resolvedLanguage) {
            case 'fr':
            case 'fr-FR':
                setLocale(AG_GRID_LOCALE_FR);
                break;
            case 'it':
            case 'it-IT':
                setLocale(AG_GRID_LOCALE_IT);
                break;
            case 'en':
            default:
                setLocale(AG_GRID_LOCALE_EN);
        }
    }, [i18n.resolvedLanguage]);

    useEffect(() => {
        if (height !== undefined) {
            setTableHeight(height);
        } else if (wrapperRef && wrapperRef.current) {
            setTableHeight(window.innerHeight - wrapperRef.current.getBoundingClientRect().top - 20 + 'px');
        }
    }, [wrapperRef, height]);

    useEffect(() => {
        setRender(true);
    }, [locale]);

    return render ? (
        <Paper className={'ag-theme-alpine'}
               elevation={1}
               style={{
                   height: !!height ? tableHeight : '100%',
               }}
               ref={wrapperRef}
               sx={{
                   '& .ag-status-bar': {
                       overflow: 'auto',
                   }
               }}
        >
            {toolbar &&
                <AgGridCustomToolbar gridRef={ref} renderInfo={toolbar.renderInfo}
                                     renderActions={toolbar.renderActions}/>
            }
            <AgGridReact
                domLayout={!height ? 'autoHeight' : 'normal'}
                ref={ref}
                pagination={true}
                paginationPageSize={10}
                suppressPaginationPanel={true}
                suppressContextMenu={true}
                cacheBlockSize={10}
                enableCellTextSelection={true}
                unSortIcon={true}
                suppressDragLeaveHidesColumns={true}
                serverSideInfiniteScroll={true}
                {...agGridProps}
                statusBar={agGridProps.pagination === false || agGridProps.gridOptions?.pagination === false ? undefined : statusBar}
                localeText={locale}
                defaultColDef={defaultColDef}
                onGridReady={handleGridReady}/>
        </Paper>
    ) : (<Skeleton variant="rectangular" height={400}/>);
};

export default AgGridCustomTable;